import { useCallback, useEffect, useRef } from 'react';

/**
 * True when a Radix popper layer that has *taken over outside pointer events*
 * — Select, DropdownMenu, or a modal Popover — is currently open. Two signals
 * must both hold:
 *
 *   1. A Radix popper is mounted: `[data-radix-popper-content-wrapper]` exists.
 *   2. That popper disabled outside pointer events: `document.body` carries
 *      the `pointer-events: none` its `DismissableLayer` sets while open.
 *
 * Both conditions together are deliberate:
 *   - The wrapper alone is too broad — a hover `Tooltip` also renders it but
 *     runs with `disableOutsidePointerEvents: false`, so it never steals the
 *     outside click; clicking away from a tooltip must still dismiss the parent
 *     overlay. Requiring the body lock excludes it.
 *   - The body lock alone is too broad — a lone modal `Dialog` sets it too, but
 *     renders no popper wrapper (it is CSS-centered, not popper-positioned), so
 *     requiring the wrapper excludes the "just the Dialog is open" case and
 *     keeps ordinary backdrop-dismiss working.
 *
 * The net set is exactly the layers that race the parent's outside-dismiss.
 */
export function isOutsideBlockingLayerOpen(doc: Document = document): boolean {
  return (
    doc.querySelector('[data-radix-popper-content-wrapper]') !== null
    && doc.body.style.pointerEvents === 'none'
  );
}

/**
 * True when `container` holds an open legacy `components/Modal` panel.
 *
 * That Modal is hand-rolled and renders INLINE rather than through a portal,
 * so when one is opened from inside a `Drawer` it becomes a DOM descendant of
 * the drawer's content instead of a sibling layer. It is also not part of
 * Radix's dismissable-layer stack, so Radix cannot see it and both surfaces
 * answer the same Escape — closing the drawer out from under a half-filled
 * form. Consulting the DOM is what makes the nested modal visible to the
 * parent at all.
 *
 * Matches on the modal's own dialog semantics (`role="dialog"` +
 * `aria-modal`), which is the contract Modal guarantees, not on a class name
 * or test id.
 */
export function hasNestedLegacyModal(container: HTMLElement | null): boolean {
  return container !== null
    && container.querySelector('[role="dialog"][aria-modal="true"]') !== null;
}

/**
 * Guard for overlay surfaces against the Radix "nested Select closes the parent
 * Dialog on a single outside click" bug class (radix-ui/primitives#1088 /
 * #2961 / #3461 — still open upstream for touch / cross-browser).
 *
 * Why a live DOM probe at dismiss time is not enough: Radix Dialog defers its
 * outside-dismiss to the trusted `click`, but a nested Select closes earlier on
 * `pointerdown` (synchronously, inside `flushSync`) and removes its popper from
 * the DOM. By the time the parent's deferred dismiss runs, the popper is gone,
 * so probing then reports "nothing open" and the parent dismisses — the bug.
 *
 * So we snapshot "was an outside-blocking layer open" at `pointerdown` in the
 * CAPTURE phase, which runs before the Select's own bubble-phase dismiss, and
 * consult that snapshot when the deferred dismiss fires. The snapshot is
 * refreshed on every press, so it never goes stale across interactions. See
 * `isOutsideBlockingLayerOpen` for why the gate needs both the popper wrapper
 * and the body pointer-events lock (excludes hover Tooltips and lone Dialogs).
 */
export function useOverlayDismissGuard(open: boolean): {
  /** Wire to a Radix `onInteractOutside` — preventDefault blocks the dismiss. */
  readonly onInteractOutside: (event: Event) => void;
  /** For hand-rolled backdrops: true when the current press must not dismiss. */
  readonly shouldBlockDismiss: () => boolean;
} {
  const nestedOpenAtPressRef = useRef(false);

  useEffect(() => {
    if (!open) {
      nestedOpenAtPressRef.current = false;
      return;
    }
    // Snapshot in the CAPTURE phase of both dismissal-initiating gestures
    // (pointer press and Escape key). Capture runs before the nested layer's
    // own bubble/capture dismiss removes its popper, so the snapshot reflects
    // the state at the start of the gesture, not after the layer has closed.
    const capture = (): void => {
      nestedOpenAtPressRef.current = isOutsideBlockingLayerOpen();
    };
    document.addEventListener('pointerdown', capture, { capture: true });
    document.addEventListener('keydown', capture, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', capture, { capture: true });
      document.removeEventListener('keydown', capture, { capture: true });
    };
  }, [open]);

  const onInteractOutside = useCallback((event: Event) => {
    if (nestedOpenAtPressRef.current) {
      event.preventDefault();
    }
  }, []);

  const shouldBlockDismiss = useCallback(() => nestedOpenAtPressRef.current, []);

  return { onInteractOutside, shouldBlockDismiss };
}
